<template>
  <v-container fluid>
    <v-row>
      <!-- Date Range -->
      <v-col cols="12" sm="6">
        <v-menu
          v-model="fromDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="localRestriction.fromDate"
              :label="$t('rate_plans.restriction_from_date')"
              readonly
              v-bind="attrs"
              :rules="[v => !!v || $t('rate_plans.from_date_required')]"
              required
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="localRestriction.fromDate"
            @input="fromDateMenu = false"
          />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6">
        <v-menu
          v-model="untilDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="localRestriction.untilDate"
              :label="$t('rate_plans.restriction_until_date')"
              readonly
              v-bind="attrs"
              :rules="[v => !!v || $t('rate_plans.until_date_required')]"
              required
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="localRestriction.untilDate"
            @input="untilDateMenu = false"
          />
        </v-menu>
      </v-col>

      <!-- Availability Status -->
      <v-col cols="12" sm="6">
        <v-select
          v-model="localRestriction.availabilityStatus"
          :items="availabilityStatusOptions"
          :label="$t('rate_plans.availability_status')"
          item-text="text"
          item-value="value"
          :hint="$t('rate_plans.availability_status_hint')"
          persistent-hint
        />
      </v-col>

      <!-- Arrival/Departure Controls -->
      <v-col cols="12" sm="6">
        <v-switch
          v-model="localRestriction.arrivalsAllowed"
          :label="$t('rate_plans.arrivals_allowed')"
          :hint="$t('rate_plans.arrivals_allowed_hint')"
          persistent-hint
        />
      </v-col>

      <v-col cols="12" sm="6">
        <v-switch
          v-model="localRestriction.departuresAllowed"
          :label="$t('rate_plans.departures_allowed')"
          :hint="$t('rate_plans.departures_allowed_hint')"
          persistent-hint
        />
      </v-col>

      <!-- Stay Requirements -->
      <v-col cols="12">
        <v-divider class="mb-4"></v-divider>
        <h4 class="text-subtitle-1 mb-3">
          {{ $t('rate_plans.stay_requirements') }}
        </h4>
      </v-col>

      <v-col cols="12" sm="3">
        <v-text-field
          v-model.number="localRestriction.minimumStay"
          :label="$t('rate_plans.minimum_stay')"
          type="number"
          min="1"
          :hint="$t('rate_plans.minimum_stay_hint')"
          persistent-hint
        />
      </v-col>

      <v-col cols="12" sm="3">
        <v-text-field
          v-model.number="localRestriction.maximumStay"
          :label="$t('rate_plans.maximum_stay')"
          type="number"
          min="1"
          :hint="$t('rate_plans.maximum_stay_hint')"
          persistent-hint
        />
      </v-col>

      <v-col cols="12" sm="3">
        <v-text-field
          v-model.number="localRestriction.minimumStayThrough"
          :label="$t('rate_plans.minimum_stay_through')"
          type="number"
          min="1"
          :hint="$t('rate_plans.minimum_stay_through_hint')"
          persistent-hint
        />
      </v-col>

      <v-col cols="12" sm="3">
        <v-text-field
          v-model.number="localRestriction.maximumStayThrough"
          :label="$t('rate_plans.maximum_stay_through')"
          type="number"
          min="1"
          :hint="$t('rate_plans.maximum_stay_through_hint')"
          persistent-hint
        />
      </v-col>

      <!-- Advance Booking Rules -->
      <v-col cols="12">
        <v-divider class="mb-4"></v-divider>
        <h4 class="text-subtitle-1 mb-3">
          {{ $t('rate_plans.advance_booking_rules') }}
        </h4>
      </v-col>

      <v-col cols="12" sm="6">
        <v-text-field
          v-model.number="localRestriction.minDaysBeforeArrival"
          :label="$t('rate_plans.min_days_before_arrival')"
          type="number"
          min="0"
          :hint="$t('rate_plans.min_days_before_arrival_hint')"
          persistent-hint
        />
      </v-col>

      <v-col cols="12" sm="6">
        <v-text-field
          v-model.number="localRestriction.maxDaysBeforeArrival"
          :label="$t('rate_plans.max_days_before_arrival')"
          type="number"
          min="0"
          :hint="$t('rate_plans.max_days_before_arrival_hint')"
          persistent-hint
        />
      </v-col>

      <!-- Actions -->
      <v-col cols="12">
        <v-divider class="mb-4"></v-divider>
        <div class="d-flex justify-end">
          <v-btn color="error" text @click="removeRestriction">
            {{ $t('rate_plans.remove_restriction') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'RestrictionForm',

  props: {
    restriction: {
      type: Object,
      required: false,
      default: () => ({
        fromDate: '',
        untilDate: '',
        availabilityStatus: 'open',
        arrivalsAllowed: true,
        departuresAllowed: true,
        minimumStay: null,
        maximumStay: null,
        minimumStayThrough: null,
        maximumStayThrough: null,
        maxDaysBeforeArrival: null,
        minDaysBeforeArrival: null,
      }),
    },
  },

  data() {
    return {
      fromDateMenu: false,
      untilDateMenu: false,

      availabilityStatusOptions: [
        { text: 'Open', value: 'open' },
        { text: 'Closed', value: 'closed' },
      ],

      localRestriction: {
        fromDate: null,
        untilDate: null,
        availabilityStatus: 'open',
        arrivalsAllowed: true,
        departuresAllowed: true,
        minimumStay: 1,
        maximumStay: null,
        minimumStayThrough: 1,
        maximumStayThrough: null,
        maxDaysBeforeArrival: null,
        minDaysBeforeArrival: null,
      },
    }
  },

  watch: {
    restriction: {
      handler(newRestriction) {
        this.localRestriction = { ...newRestriction }
      },
      immediate: true,
    },

    localRestriction: {
      handler(newRestriction) {
        this.$emit('update', newRestriction)
      },
      deep: true,
    },
  },

  methods: {
    removeRestriction() {
      this.$emit('remove')
    },
  },
}
</script>
